<template>
  <div
    class="input"
    :class="`input--${type} ${inline ? 'input--inline' : ''}`"
    data-invalid-class="input--invalid"
  >
    <label
      v-show="type !== 'hidden'"
      class="input__label"
      :for="inputId">
      {{ label }}
    </label>
    <span
      v-show="type !== 'hidden'"
      class="input__error"
    >
      {{ fieldErrorMessage }}
    </span>
    <textarea
      v-if="type === 'textarea'"
      class="input__textarea"
      :name="name"
      v-model="value"
      @input="onInput"
      :required="required"
      :readonly="readonly"
      :hidden="hidden"
      :inputmode="inputmode"
      :id="inputId"
      :disabled="disabled"
      ref="input" />
    <input
      v-else
      class="input__field"
      v-model="value"
      :name="name"
      :type="inputType"
      @input="onInput"
      @keypress.enter="onPressEnter"
      :required="required"
      :readonly="readonly"
      :hidden="hidden"
      :autocomplete="autocomplete"
      :inputmode="inputmode"
      :id="inputId"
      :disabled="disabled"
      :placeholder="placeholder"
      ref="input"
    >

    <ag-button
      title="Passwort anzeigen"
      v-if="type === 'password'"
      tabindex="-1"
      class="input__password-btn"
      @mousedown.native="showPassword(true)"
      @touchstart.native="showPassword(true)"
      @mouseup.native="showPassword(false)"
      @mouseout.native="showPassword(false)"
      @touchend.native="showPassword(false)"
    >
      <ag-icon
        name="eye"
        modifier="eye"
      />
    </ag-button>
  </div>
</template>

<script>
import AbstractInput from './AbstractInput.vue';
import AgButton from '../shared/AgButton.vue';
import AgIcon from '../shared/AgIcon.vue';

/**
 * @vuese
 * a text(ual) input component. Usage for `text`,`email`,`url`,`password`, etc.
 * input fields in your form-wrapper.
 * Most javascript is inherit from `AbstractInput.vue` component
 */
export default {
  name: 'form-input',
  components: {
    AgButton,
    AgIcon,
  },
  props: {
    autocomplete: {
      type: String,
      default: null,
      required: false,
    },
  },
  extends: AbstractInput,
  data() {
    return {
      inputType: this.type,
    };
  },
  methods: {
    onInput(event) {
      // if you bind a v-model to this component or just want get updates on input changes
      // @arg event.target.value
      this.$emit('input', event.target.value);
    },
    onPressEnter(event) {
      // if you bind a v-model to this component or just want get updates on input changes
      // @arg event
      this.$emit('keypress-enter', event);
    },
    showPassword(toggle) {
      if (toggle) {
        this.inputType = 'text';
      } else {
        this.inputType = this.type;
        this.$nextTick(() => {
          this.$refs.input.focus();
        });
      }
    },
  },
};
</script>

<style lang="scss">
.input {
  display: block;
  width: 100%;
  position: relative;

  /* this is not BEM, it uses cascade for invalidation showing */
  $this: &;

  &--invalid {
    #{$this}__error {
      display: block;
    }

    #{$this}__field:invalid {
      border: 2px solid $border-error-color;
      background-color: $error-input-bg;
    }
  }

  &--error-state {
    input {
      border: 2px solid $border-error-color;
      background-color: $error-input-bg;
    }
  }

  &__label {
    display: block;
    color: $primary-color;
    font-size: rem(14px);
    line-height: 1.35;
    font-weight: 600;
    margin-bottom: em(8.75, 14px);
  }

  &__error {
    display: none;
    color: $error-text-color;
    font-size: rem(13px);
    line-height: 1.38;
    margin-bottom: em(8.75, 13px);
  }

  &__field,
  &__textarea {
    font-family: $copy-font;
    background-color: $bg-light-color;
    border: 2px solid rgba(123, 142, 169, .3);
    font-size: 1em;
    width: 100%;
    height: em(45px);
    padding: em(12px) em(15px);

    &:active,
    &:focus {
      border: 1px solid $brand-color;
    }
  }

  &__textarea {
    height: em(90px);
  }

  &__password-btn {
    border-color: transparent;
    display: inline-flex;
    background: transparent;
    position: absolute;
    width: auto;
    padding: 0;
    right: rem(10px);
    bottom: rem(12px);
  }
}
</style>
